import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

// ── Types ──────────────────────────────────────────────────────────────────

export type ChecklistSectionKey =
  | 'companyProfileCompleted'
  | 'moveTypesCompleted'
  | 'fleetCompleted'
  | 'residentialSettingsCompleted'
  | 'commercialSettingsCompleted'
  | 'paymentsCompleted'
  | 'materialsPricingCompleted';

export interface OrgSetupChecklist {
  companyProfileCompleted: boolean;
  moveTypesCompleted: boolean;
  fleetCompleted: boolean;
  residentialSettingsCompleted: boolean;
  commercialSettingsCompleted: boolean;
  paymentsCompleted: boolean;
  materialsPricingCompleted: boolean;
  completedAt: string | null;
  skippedAt: string | null;
}

export interface OrgSetupChecklistStatus extends OrgSetupChecklist {
  applicableSections: ChecklistSectionKey[];
  isComplete: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useSetupChecklist(orgId: string | undefined) {
  const [checklist, setChecklist] = useState<OrgSetupChecklistStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: OrgSetupChecklistStatus }>(
        `/org-admin/${orgId}/setup-checklist`,
      );
      setChecklist(res.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch setup checklist');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateSection = useCallback(async (updates: Partial<Record<ChecklistSectionKey, boolean>>) => {
    if (!orgId) return;
    try {
      const res = await api.patch<{ success: boolean; data: OrgSetupChecklistStatus }>(
        `/org-admin/${orgId}/setup-checklist`,
        updates,
      );
      setChecklist(res.data);
    } catch (err) {
      console.error('[useSetupChecklist] updateSection error:', err);
    }
  }, [orgId]);

  const dismiss = useCallback(async () => {
    if (!orgId) return;
    try {
      await api.post(`/org-admin/${orgId}/setup-checklist/dismiss`);
      await fetch();
    } catch (err) {
      console.error('[useSetupChecklist] dismiss error:', err);
    }
  }, [orgId, fetch]);

  const complete = useCallback(async () => {
    if (!orgId) return;
    try {
      await api.post(`/org-admin/${orgId}/setup-checklist/complete`);
      await fetch();
    } catch (err) {
      console.error('[useSetupChecklist] complete error:', err);
    }
  }, [orgId, fetch]);

  const completedCount = checklist
    ? checklist.applicableSections.filter((k) => checklist[k]).length
    : 0;
  const totalCount = checklist?.applicableSections.length ?? 0;

  return {
    checklist,
    loading,
    error,
    refetch: fetch,
    updateSection,
    dismiss,
    complete,
    completedCount,
    totalCount,
    isComplete: checklist?.isComplete ?? false,
  };
}
